import React, { useEffect, useRef, useState } from 'react';
import ConfirmationModal from './ConfirmationModal';
import styles from './ResolvePeakConfirm.module.css';

/**
 * US2028896: Resolve-time confirmation of the highest EP level (peak) reached.
 *
 * Presented when the MIM resolves an incident (sets Impact End). Displays the
 * system-computed peak and asks the MIM to confirm it. Choosing "No / Incorrect"
 * reveals an EP1–EP4 dropdown so the MIM can correct the value before confirming.
 * Confirmation is mandatory — the resolve does not proceed until the MIM confirms
 * (Yes) or corrects (No → dropdown → Confirm). Cancel/Escape aborts the resolve.
 *
 * Thin wrapper over the shared ConfirmationModal (focus trap, Escape, portal,
 * WCAG focus restoration) — this component only adds the EP correction UI and
 * hands the confirmed value back to the caller.
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Called when the resolve is aborted (cancel/Escape/backdrop)
 * @param {function} onConfirm - Called with the confirmed EP value (e.g. "EP1")
 * @param {string} computedPeak - System-computed peak from the audit trail (may be
 *        empty). Snapshotted on each closed→open transition; later changes to
 *        this prop are ignored for the life of the open session, so an async
 *        parent write cannot change the modal's mode or its submitted value
 *        under the MIM.
 * @param {Array<{value: string, label: string}>} epOptions - Optional EP options
 *        for the correction dropdown (defaults to EP1–EP4).
 */
const DEFAULT_EP_OPTIONS = [
  { value: 'EP1', label: 'EP1' },
  { value: 'EP2', label: 'EP2' },
  { value: 'EP3', label: 'EP3' },
  { value: 'EP4', label: 'EP4' },
];

const ResolvePeakConfirm = ({
  isOpen,
  onClose,
  onConfirm,
  computedPeak,
  epOptions = DEFAULT_EP_OPTIONS,
}) => {
  // correcting=false → confirming the computed peak (Yes path).
  // correcting=true  → the MIM said No/Incorrect and is picking a corrected EP.
  const [correcting, setCorrecting] = useState(false);
  const [selectedEP, setSelectedEP] = useState('');
  const selectRef = useRef(null);
  // Tracks the previous isOpen so the latch + reset below fire on the
  // closed→open TRANSITION only. See the comment on the latch.
  const wasOpenRef = useRef(false);
  // The peak this session is about, captured once when the modal opened.
  const latchedPeakRef = useRef('');
  // Set by the latch, consumed by the reset effect below.
  const pendingResetRef = useRef(false);

  // `computedPeak` is a LIVE prop that the parent rewrites from three
  // independent async paths (the field auto-save PATCH response, the deferred
  // post-communication refetch, and a version restore), any of which can land
  // while the modal is open. Everything this component shows and submits is
  // therefore derived from `latchedPeakRef` — a snapshot taken on the
  // closed→open transition — never from the live prop.
  //
  // Reading the live prop instead let an in-flight write change the modal's
  // MODE under the MIM. On an incident with no recorded peak the modal opens in
  // dropdown mode; if a write then supplied a peak, `showDropdown` flipped
  // false, the select unmounted, the button relabelled from "Confirm" to "Yes,
  // that is correct" in the same screen position, and the next click submitted
  // the newly arrived peak instead of the EP the MIM had selected — failing
  // "Resolving records the peak EP the MIM confirmed."
  //
  // The latch runs during RENDER, not in the effect below, deliberately: the
  // derived values are computed during render, so a latch written in an effect
  // would leave the first committed frame deriving from the PREVIOUS session's
  // snapshot — showing the wrong mode (and firing the focus effect on a select
  // that is about to unmount) until the effect's state writes forced a second
  // render. Writing a ref during render is safe here because the write is
  // idempotent: StrictMode's double invoke latches the same value.
  if (isOpen && !wasOpenRef.current) {
    latchedPeakRef.current = computedPeak || '';
    pendingResetRef.current = true;
  }
  wasOpenRef.current = isOpen;
  const latchedPeak = latchedPeakRef.current;

  // Seed internal state on each closed→open transition so a prior correction
  // does not leak into a new resolve.
  //
  // Gated on the transition (via the flag the latch arms), not on `isOpen`
  // being true: keying the reset on `computedPeak` changing meant an async
  // write re-ran the reset mid-session — clearing `correcting` and overwriting
  // `selectedEP`, silently discarding a correction the MIM was in the middle of
  // making. Once open, the MIM owns these two values; nothing resets them until
  // the modal closes and reopens.
  //
  // The seed comes from the LATCHED peak, not the live prop. The prop can
  // change between the latch and this effect, and seeding `selectedEP` from a
  // peak the latch did not capture would put a value the MIM never picked into
  // `confirmedValue` while `showDropdown` (derived from the latch) is true.
  useEffect(() => {
    if (pendingResetRef.current) {
      pendingResetRef.current = false;
      setCorrecting(false);
      setSelectedEP(latchedPeakRef.current);
    }
  }, [isOpen]);

  const hasComputedPeak = Boolean(latchedPeak);

  // When there is no computed peak, force the correction dropdown immediately.
  const showDropdown = correcting || !hasComputedPeak;

  // A11y: move focus to the EP select when it becomes visible so keyboard/SR
  // users get a clear cue after clicking "No / Incorrect" (the toggle button
  // that had focus is removed on reveal).
  useEffect(() => {
    if (isOpen && showDropdown && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isOpen, showDropdown]);

  if (!isOpen) return null;

  // showDropdown is the single source of truth for which mode the modal is in.
  // Both values below derive from it: keying either off `correcting` alone
  // desynchronizes it from the UI actually on screen.

  // The value that will be submitted on confirm.
  const confirmedValue = showDropdown ? selectedEP : latchedPeak;

  // Whenever the dropdown is showing, the MIM's selection is what satisfies the
  // gate, so require one. Otherwise we are confirming the computed peak, which
  // must exist. A missing computed peak is a supported case, not a defensive one:
  // the component deliberately shows the dropdown for it, and picking an EP there
  // is the resolve path. Requiring hasComputedPeak in that case would leave
  // Confirm permanently disabled with no UI path to satisfy the gate.
  const confirmDisabled = showDropdown ? !selectedEP : !hasComputedPeak;

  const message = hasComputedPeak
    ? `The highest EP level recorded for this incident is ${latchedPeak}. Was ${latchedPeak} the highest EP level reached?`
    : 'No EP level was recorded for this incident. Select the highest EP level reached to resolve.';

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(confirmedValue);
  };

  // "No / Incorrect": clear the selection so the rejected computed peak is not
  // silently re-selected. Confirm stays disabled until the MIM deliberately
  // picks a valid EP from the dropdown.
  const handleReject = () => {
    setSelectedEP('');
    setCorrecting(true);
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Confirm Peak Priority"
      message={message}
      confirmText={showDropdown ? 'Confirm' : 'Yes, that is correct'}
      cancelText="Cancel"
      variant="primary"
      confirmDisabled={confirmDisabled}
    >
      {!showDropdown && (
        <button
          type="button"
          className={styles.correctionToggle}
          onClick={handleReject}
        >
          No / Incorrect — choose the correct EP level
        </button>
      )}

      {showDropdown && (
        <div className={styles.correctionRow}>
          <label htmlFor="resolve-peak-ep-select" className={styles.correctionLabel}>
            Highest EP level reached
          </label>
          <select
            id="resolve-peak-ep-select"
            ref={selectRef}
            aria-label="Highest EP level reached"
            className={styles.correctionSelect}
            value={selectedEP}
            onChange={(e) => setSelectedEP(e.target.value)}
          >
            <option value="" disabled>
              Select EP level…
            </option>
            {epOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </ConfirmationModal>
  );
};

export default ResolvePeakConfirm;
